import { FC, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import styles from "./SelectedSearch.module.scss";
import { ContrastText } from "src/components";
import { TrackerKeywordsDataSourcesIcon, EditSearchModal } from "src/features";
import { selectLocationById, selectLanguageById } from "src/store/selectors";
import { useModal } from "src/hooks";
import { isSearchCreatedTypeGuard } from "src/utils";
import { Pencil } from "src/assets/icons";

type Props = {
  search: Search.CreationData;
  updateSearchHandler?: ({
    id,
    changes,
  }: {
    id: Search.Data["id"];
    changes: Search.CreationData;
  }) => void;
};

export const SelectedSearch: FC<Props> = ({ search, updateSearchHandler }) => {
  const { t } = useTranslation();
  const { setModal } = useModal();

  const location = useSelector((state: Store.RootState) =>
    selectLocationById(state, search.locationId),
  );

  const language = useSelector((state: Store.RootState) =>
    selectLanguageById(state, search.languageId),
  );

  const locationName = useMemo<string>(
    () => location?.name || "",
    [location?.name],
  );

  const languageName = useMemo<string>(
    () => language?.name || "",
    [language?.name],
  );

  const showEditSearchModal = useCallback((): void => {
    if (!isSearchCreatedTypeGuard(search) || !updateSearchHandler) return;

    return setModal(
      "edit-search",
      <EditSearchModal search={search} submitHandler={updateSearchHandler} />,
    );
  }, [search, setModal, updateSearchHandler]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.content}>
        <ContrastText className={styles.heading}>
          {t("component.selected_search.label.search")}
        </ContrastText>
        <span className={styles.title}>{search.subject}</span>
      </div>
      <div className={styles.descriptionWrapper}>
        <span className={styles.description}>{search.description}</span>
        {updateSearchHandler && (
          <button
            type="button"
            onClick={showEditSearchModal}
            className={styles.editIcon}
            aria-label={t("component.selected_search.label.edit_search")}
          >
            <Pencil />
          </button>
        )}
      </div>
      <div className={styles.settings}>
        <div className={styles.keywordsDataSource}>
          <TrackerKeywordsDataSourcesIcon
            keywordsDataSources={[search.keywordsDataSource]}
          />
        </div>
        <div className={styles.location} title={locationName}>
          <span>{locationName}</span>
        </div>
        <div className={styles.language} title={languageName}>
          <span>{search.languageId.toUpperCase()}</span>
        </div>
      </div>
    </div>
  );
};
